import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Compass, Lightbulb, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TravelStoryDetail = () => {
  const { slug } = useParams();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchStory = async () => {
    try {
      const response = await axios.get(`${API}/travel-stories`);
      const foundStory = response.data.find(s => s.slug === slug);
      setStory(foundStory);
    } catch (error) {
      console.error('Error fetching story:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E6FBA]" />
      </div>
    );
  }

  if (!story) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl mb-4">Story not found</h1>
        <Link to="/travel-stories" className="text-[#1E6FBA] hover:underline">
          Back to Travel Stories
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="travel-story-detail">
      {/* Hero */}
      <section className="relative h-[70vh] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${story.hero_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 w-full">
          <Link to="/travel-stories" className="inline-flex items-center text-white hover:text-[#F28A30] mb-6 transition-colors" data-testid="back-link">
            <ArrowLeft className="w-5 h-5 mr-2" />
            <span>Back to Stories</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[#F28A30] font-semibold mb-2 uppercase tracking-wider">{story.country}</p>
            <h1 className="hero-title text-5xl sm:text-6xl lg:text-7xl text-white mb-4" data-testid="story-title">
              {story.destination}
            </h1>
            <p className="text-xl text-white/90 max-w-3xl">{story.excerpt}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Experiences */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
          data-testid="experiences-section"
        >
          <div className="flex items-center mb-6">
            <Compass className="w-6 h-6 text-[#1E6FBA] mr-3" />
            <h2 className="text-3xl" style={{ fontFamily: 'Playfair Display, serif' }}>What You Can Experience</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {story.experiences.map((exp, index) => (
              <div key={index} className="bg-[#F5E9D4] p-6 rounded-xl">
                <p className="text-[#1A1A1A]">{exp}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Highlights */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
          data-testid="highlights-section"
        >
          <div className="flex items-center mb-6">
            <MapPin className="w-6 h-6 text-[#2E8B57] mr-3" />
            <h2 className="text-3xl" style={{ fontFamily: 'Playfair Display, serif' }}>Top Highlights</h2>
          </div>
          <ul className="space-y-3">
            {story.highlights.map((highlight, index) => (
              <li key={index} className="flex items-start">
                <span className="inline-block w-2 h-2 bg-[#2E8B57] rounded-full mt-2 mr-4 flex-shrink-0"></span>
                <span className="text-lg text-[#1A1A1A]">{highlight}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Tips */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
          data-testid="tips-section"
        >
          <div className="flex items-center mb-6">
            <Lightbulb className="w-6 h-6 text-[#F28A30] mr-3" />
            <h2 className="text-3xl" style={{ fontFamily: 'Playfair Display, serif' }}>Travel Tips</h2>
          </div>
          <div className="bg-white p-8 rounded-2xl shadow-sm">
            <ul className="space-y-4">
              {story.tips.map((tip, index) => (
                <li key={index} className="flex items-start">
                  <span className="inline-block bg-[#F28A30] text-white font-bold text-sm w-6 h-6 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    {index + 1}
                  </span>
                  <span className="text-[#1A1A1A]">{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* Gallery */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          data-testid="gallery-section"
        >
          <h2 className="text-3xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Gallery</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {story.gallery.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`${story.destination} ${index + 1}`}
                className="w-full h-64 object-cover rounded-xl"
              />
            ))}
          </div>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="bg-[#1E6FBA] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Want to Visit {story.destination}?
            </h2>
            <p className="text-white/90 text-lg mb-6">
              Let us help you plan your perfect trip with insider tips and custom itineraries.
            </p>
            <Link to="/contact">
              <button className="btn-scale bg-white text-[#1E6FBA] hover:bg-gray-100 rounded-full px-8 py-4 font-medium" data-testid="cta-contact">
                Get Travel Planning Help
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TravelStoryDetail;
